import {
  AvailableCarrierPayload,
  AvailableCarrierResponse,
  CheckoutDetails,
  DeliveryRatesPayload,
  ICancelOrder,
  OrderDetailsResponse,
  OrderTrackingResponse,
  ResponseSuccess,
  ValidateAddressData,
  ValidateAddressPayload
} from '../../TYPES/api.body';
import {
  BUSINESS_SUBMIT_CART,
  CANCEL_ORDER,
  GET_ALL_ORDERS,
  GET_AVAILABLE_CARRIERS,
  GET_CANCELLED_ORDERS,
  GET_COMPLETED_ORDERS,
  GET_DELIVERY_AGENTS,
  GET_ONGOING_ORDERS,
  GET_PENDING_ORDERS,
  GET_SHIPBUBBLE_CATEGORIES,
  GET_SHIPBUBBLE_DELIVERY_RATES,
  GET_SHIPBUBBLE_DIMENSIONS,
  GET_SINGLE_ORDER,
  SUBMIT_ORDER,
  VALIDATE_ADDRESS,
  VALIDATE_ADDRESS_DELIVERY
} from '../config/endpoints';
import { getMadinaOrder, getMadinaOrders, getMadinaOrderTracking } from 'network/services/madina';
import type { MadinaOrder } from 'TYPES/madina';
import { mapMadinaOrderToLegacy, mapMadinaTrackingToLegacy } from 'utils/madinaMappers';
import api from '../config/setAuthHeader';

export const getOrderHistories = async (args?: { size?: number; page?: number }): Promise<OrderDetailsResponse[]> => {
  return listOrdersForTab(GET_ALL_ORDERS, args);
};

export const getPendingOrders = async (args?: { size?: number; page?: number }) => {
  const { data } = await api.get(`${GET_PENDING_ORDERS}?pageSize=${args?.size ?? 10}&page=${args?.page ?? 0}`);
  return data.data;
};
const readLegacyOrderList = (payload: unknown): OrderDetailsResponse[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as { data?: unknown; orders?: unknown };
    if (Array.isArray(record.data)) return record.data as OrderDetailsResponse[];
    if (Array.isArray(record.orders)) return record.orders as OrderDetailsResponse[];
  }
  return [];
};

const listMadinaOrdersForTab = async (
  args: { size?: number; page?: number } | undefined,
  status?: string
): Promise<OrderDetailsResponse[]> => {
  const orders = await getMadinaOrders({
    per_page: args?.size ?? 20,
    page: (args?.page ?? 0) + 1,
    status
  });
  return orders.map(mapMadinaOrderToLegacy);
};

const listOrdersForTab = async (
  legacyPath: string,
  args: { size?: number; page?: number } | undefined,
  status?: string
): Promise<OrderDetailsResponse[]> => {
  const [madinaResult, legacyResult] = await Promise.allSettled([
    listMadinaOrdersForTab(args, status),
    api.get(`${legacyPath}?pageSize=${args?.size ?? 10}&page=${args?.page ?? 0}`)
  ]);

  const madina = madinaResult.status === 'fulfilled' ? madinaResult.value : [];
  const legacy =
    legacyResult.status === 'fulfilled' ? readLegacyOrderList(legacyResult.value.data?.data ?? legacyResult.value.data) : [];

  const seen = new Set<number>();
  return [...madina, ...legacy].filter(order => {
    if (seen.has(order.id)) return false;
    seen.add(order.id);
    return true;
  });
};

export const getOngoingOrders = async (args?: { size?: number; page?: number }): Promise<OrderDetailsResponse[]> => {
  return listOrdersForTab(GET_ONGOING_ORDERS, args, 'ongoing');
};
export const getCompletedOrders = async (args?: { size?: number; page?: number }): Promise<OrderDetailsResponse[]> => {
  return listOrdersForTab(GET_COMPLETED_ORDERS, args, 'completed');
};
export const getCancelledOrders = async (args?: { size?: number; page?: number }): Promise<OrderDetailsResponse[]> => {
  const { data } = await api.get(`${GET_CANCELLED_ORDERS}?pagesize=${args?.size ?? 10}&page=${args?.page ?? 0}`);
  return data.data;
};

const isRecurringMadinaOrder = (order: MadinaOrder): boolean => {
  const extra = order as MadinaOrder & {
    recurring?: boolean;
    recurring_delivery?: boolean | string;
    is_recurring?: boolean;
    delivery_schedule?: string;
  };
  if (extra.recurring === true || extra.is_recurring === true || extra.recurring_delivery === true) {
    return true;
  }
  const schedule = String(extra.delivery_schedule ?? extra.recurring_delivery ?? '').trim().toLowerCase();
  return schedule.length > 0 && schedule !== 'none' && schedule !== 'false';
};

export const getRecurringOrders = async (args?: { size?: number; page?: number }): Promise<OrderDetailsResponse[]> => {
  try {
    const orders = await getMadinaOrders({
      per_page: args?.size ?? 10,
      page: (args?.page ?? 0) + 1
    });
    return orders.filter(isRecurringMadinaOrder).map(mapMadinaOrderToLegacy);
  } catch {
    return [];
  }
};

export const submitOrder = async (formData: CheckoutDetails) => {
  const { customerType, ...rest } = formData;
  if (customerType === 'BUSINESS') {
    return await api.post(BUSINESS_SUBMIT_CART, rest);
  }
  return await api.post(SUBMIT_ORDER, rest);
};

const placedAt = (order?: { order_date?: string; created_at?: string; orderDate?: string } | null) => {
  const value = String(order?.order_date ?? order?.created_at ?? order?.orderDate ?? '').trim();
  if (!value || Number.isNaN(new Date(value).getTime())) return '';
  return value;
};

const readOrderRef = (...values: Array<string | undefined | null>) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text && text.toUpperCase() !== 'N/A') return text;
  }
  return '';
};

export const getSingleOrder = async (id: number): Promise<OrderDetailsResponse> => {
  const { data } = await api.get(`${GET_SINGLE_ORDER}/${id}`);
  const legacy = (data?.data ?? {}) as OrderDetailsResponse & { order_ref?: string };
  let orderRef = readOrderRef(legacy.orderRef, legacy.order_ref);
  let orderDate = placedAt(legacy);

  try {
    const madina = await getMadinaOrder(id);
    orderDate = placedAt(madina) || orderDate;
    orderRef = readOrderRef(madina?.order_ref, madina?.orderRef, orderRef);
  } catch {
    // Staging has no single-order route. The reference is on tracking.
  }

  if (!orderRef) {
    try {
      const tracking = await getMadinaOrderTracking(id);
      orderDate = placedAt(tracking) || orderDate;
      orderRef = readOrderRef(tracking?.order_ref, orderRef);
    } catch {
      // Keep the legacy details when tracking is unavailable.
    }
  }

  return {
    ...legacy,
    ...(orderDate ? { orderDate } : {}),
    ...(orderRef ? { orderRef } : {})
  };
};
export const getSingleOrderdetail = async (id: number): Promise<OrderDetailsResponse> => {
  const { data } = await api.get(`${GET_SINGLE_ORDER}/${id}`);
  return data?.data;
};

export const getOrderTracking = async (id: string): Promise<OrderTrackingResponse> => {
  const orderId = Number(id);
  const tracking = await getMadinaOrderTracking(orderId);
  return mapMadinaTrackingToLegacy(tracking, id);
};
export const cancelOrder = async (formData: ICancelOrder) => {
  const { id } = formData;
  const { data } = await api.post(`${CANCEL_ORDER}/${id}`, formData);
  return data?.data;
};

export const getDeliveryAgents = async () => {
  const { data } = await api.get(GET_DELIVERY_AGENTS);
  return data.data;
};

export const getShipBubbleCategories = async () => {
  const { data } = await api.get(GET_SHIPBUBBLE_CATEGORIES);
  return data.data;
};

export const getAllCarriers = async (
  opts: AvailableCarrierPayload
): Promise<ResponseSuccess<AvailableCarrierResponse[]>> => {
  const { data } = await api.post(GET_AVAILABLE_CARRIERS, opts);
  return data;
};

export const getDeliveryRates = async (opts: DeliveryRatesPayload): Promise<ResponseSuccess<any>> => {
  const { data } = await api.post(GET_SHIPBUBBLE_DELIVERY_RATES, opts);
  return data;
};

export const getShipBubbleDimensions = async () => {
  const { data } = await api.get(GET_SHIPBUBBLE_DIMENSIONS);
  return data.data;
};

export const validateAddressCallback = async (address: string): Promise<ResponseSuccess<ValidateAddressData>> => {
  const { data } = await api.get(VALIDATE_ADDRESS.concat(address));
  return data;
};

export const validateAddressDeliveryCallback = async (opts: ValidateAddressPayload): Promise<ResponseSuccess<any>> => {
  const { data } = await api.post(VALIDATE_ADDRESS_DELIVERY, opts);
  return data;
};
